const getScannedBaseUrl = () =>
  (process.env.REACT_APP_GS_URL ||
    process.env.REACT_APP_SCAN_SEARCH_API_URL ||
    'https://ukstaging.getscanned.me').replace(/\/$/, '');

export const extractRecommendationJwt = (raw: string | null | undefined): string => {
  const str = String(raw || '').trim();
  const match = str.match(/([A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+)/);
  return match ? match[1] : str;
};

export const parseRecommendationJwtPayload = (token: string): Record<string, unknown> => {
  try {
    const segment = String(token || '').split('.')[1];
    if (!segment) return {};
    const normalized = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return {};
  }
};

const recommendationDobStorageKey = (jwt: string, uuid?: string | null) => {
  const payload = parseRecommendationJwtPayload(jwt);
  const id = uuid || (payload.uuid as string) || String(payload.recommendation_id || '');
  return id ? `pmp_dob_verified_${id}` : null;
};

export const isRecommendationDobVerified = (jwt: string, uuid?: string | null) => {
  if (!jwt) return false;
  if (parseRecommendationJwtPayload(jwt).dob_verified === true) return true;
  const key = recommendationDobStorageKey(jwt, uuid);
  if (!key) return false;
  try {
    return sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};

export const markRecommendationDobVerified = (jwt: string, uuid?: string | null) => {
  const key = recommendationDobStorageKey(jwt, uuid);
  if (!key) return;
  try {
    sessionStorage.setItem(key, '1');
  } catch {
    // ignore storage errors
  }
};

const recommendationPath = (token: string) => {
  const jwt = extractRecommendationJwt(token);
  return `${getScannedBaseUrl()}/api/v1/recommendations/${encodeURIComponent(jwt)}`;
};

export type RecommendationPayload = {
  uuid?: string;
  status?: string;
  payment_status?: string;
  paid?: boolean;
  first_name?: string;
  surname?: string;
  plan_reference?: string;
  gp_full_name?: string;
  gp_first_name?: string;
  finding_title?: string;
  finding_context?: string;
  clinical_note?: string;
  service_name?: string;
  location?: string;
  standard_price?: number | string;
  member_price?: number | string;
  clinical_rationale?: string;
  calendar_link?: string;
  aftercare_first_name?: string;
  medical_director?: string;
  stripe_link?: string;
};

type ApiResult = {
  ok: boolean;
  status: number;
  body: {
    success?: boolean;
    error?: string;
    error_code?: string;
    data?: RecommendationPayload & {
      already_paid?: boolean;
      redirect_url?: string;
      checkout_url?: string;
    };
    access_token?: string;
  };
};

const parseJson = async (response: Response) => {
  try {
    return await response.json();
  } catch {
    return {};
  }
};

export const fetchRecommendation = async (token: string): Promise<ApiResult> => {
  try {
    const response = await fetch(recommendationPath(token), {
      headers: { Accept: 'application/json' },
    });
    const body = await parseJson(response);
    return { ok: response.ok, status: response.status, body };
  } catch (error) {
    console.error('fetchRecommendation failed', error, recommendationPath(token));
    return {
      ok: false,
      status: 0,
      body: {
        success: false,
        error_code: 'network_error',
        error: 'Something went wrong loading your plan. Please try again, or contact us for a new link.',
      },
    };
  }
};

export const unlockRecommendation = async (token: string, dob: string): Promise<ApiResult> => {
  const response = await fetch(`${recommendationPath(token)}/unlock`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ dob }),
  });
  const body = await parseJson(response);
  return { ok: response.ok, status: response.status, body };
};

export const startRecommendationCheckout = async (
  token: string,
  { force = false, landing = 'corevitals' }: { force?: boolean; landing?: 'getscanned' | 'corevitals' } = {}
): Promise<ApiResult> => {
  const response = await fetch(`${recommendationPath(token)}/checkout`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ force, landing }),
  });
  const body = await parseJson(response);
  return { ok: response.ok, status: response.status, body };
};

export const fetchRecommendationPaymentStatus = async ({
  uuid,
  sessionId,
}: {
  uuid?: string | null;
  sessionId?: string | null;
} = {}): Promise<ApiResult> => {
  const params = new URLSearchParams();
  if (uuid) params.set('uuid', uuid);
  if (sessionId) params.set('session_id', sessionId);
  const response = await fetch(
    `${getScannedBaseUrl()}/api/v1/recommendations/payment_status?${params.toString()}`,
    { headers: { Accept: 'application/json' } }
  );
  const body = await parseJson(response);
  return { ok: response.ok, status: response.status, body };
};
